import React, { useCallback, useEffect, useState } from "react";
import {
  Tabs,
  Tab,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem
} from "@heroui/react";
import VoteCard from "../vote-card";
import ViewCard from "../view-cad";
import { DaoViewListApi } from "../../../api/methods-marketplace";
import VaoCardLoader from "../dao-card-loader";
import { AdjustmentsVerticalIcon } from "@heroicons/react/24/outline";

export default function DaoTabsComponent() {
  const [activePolls, setActivePolls] = useState([]);
  const [votingResults, setVotingResults] = useState([]);
  const [isLoadingActive, setIsLoadingActive] = useState(true);
  const [isLoadingInactive, setIsLoadingInactive] = useState(true);
  const [sortOption, setSortOption] = useState("Sort");
  const [selectedKey, setSelectedKey] = useState(new Set([]));
  const [selected, setSelected] = useState("active_polls");

  useEffect(() => {
    fetchVoteDetails("active", setActivePolls, setIsLoadingActive);
    fetchVoteDetails("inactive", setVotingResults, setIsLoadingInactive);
  }, []);

  const fetchVoteDetails = async (status, setData, setLoading) => {
    setLoading(true);
    try {
      const response = await DaoViewListApi(status);
      if (response) {
        setData(response?.data?.questions);
      } else {
        console.warn(`⚠️ No data returned for status: ${status}`);
      }
    } catch (error) {
      console.error(
        "❌ Failed to fetch vote details:",
        error?.response?.data || error.message || error
      );
    } finally {
      setLoading(false);
    }
  };

  const renderContent = (isLoading, data, Component, noDataText, onVoted) => {
    if (isLoading) {
      return (
        <>
          {[...Array(10)].map((_, index) => (
            <VaoCardLoader key={index} />
          ))}
        </>
      );
    }

    if (data.length > 0) {
      return data.map((item) => (
        <Component
          key={item.id}
          data={item}
          onVoted={() => onVoted && onVoted(item.id)}
        />
      ));
    }

    return (
      <p className="col-span-full text-center text-sm text-gray-500">
        {noDataText}
      </p>
    );
  };

  // Handle vote updates
  const handleVote = useCallback((questionId) => {
    // Update activePolls data
    setActivePolls((prev) =>
      prev.map((poll) =>
        poll.id === questionId ? { ...poll, user_voted_option: true } : poll
      )
    );
  }, []);

  // Sort data based on selected option
  const sortData = (data, sortType) => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];

    const dataCopy = [...data];

    switch (sortType) {
      case "newest":
        // Use end_date as fallback if created_at is unavailable
        return dataCopy.sort(
          (a, b) => new Date(b.end_date) - new Date(a.end_date)
        );

      case "endingsoon":
        return dataCopy.sort(
          (a, b) => new Date(a.end_date) - new Date(b.end_date)
        );

      case "mostvoted":
        return dataCopy.sort((a, b) => {
          const totalVotesA = a.results.reduce(
            (sum, result) => sum + (result.total_votes || 0),
            0
          );
          const totalVotesB = b.results.reduce(
            (sum, result) => sum + (result.total_votes || 0),
            0
          );
          return totalVotesB - totalVotesA;
        });

      default:
        return dataCopy;
    }
  };

  const handleSelectChange = (keys) => {
    // Extract the selected key from the Set
    const selectedKey = keys.size > 0 ? Array.from(keys)[0] : null;
    setSelectedKey(keys);

    if (!selectedKey) {
      setSortOption("Sort");
      return;
    }

    // Map the dropdown key to display text
    const SORT_LABELS = {
      newest: "Newest",
      // "endingsoon": "Ending Soon",
      mostvoted: "Most Voted"
    };

    setSortOption(SORT_LABELS[selectedKey] || "Sort");

    // Apply sorting based on which tab is active
    if (selected === "active_polls") {
      setActivePolls(sortData([...activePolls], selectedKey));
    } else {
      setVotingResults(sortData([...votingResults], selectedKey));
    }
  };

  const handleTabChange = (key) => {
    setSortOption("Sort");
    setSelectedKey(new Set([]));
    setSelected(key);
  };

  return (
    <div className="flex flex-col w-full relative">
      <Dropdown placement="bottom-end">
        <DropdownTrigger>
          <Button
            className="bg-black w-fit text-white ml-auto mb-4 sm:m-0 sm:absolute sm:top-0 sm:right-0"
            startContent={<AdjustmentsVerticalIcon className="w-4 h-4" />}
            size="sm"
            radius="full"
          >
            {sortOption}
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label="Sort Actions"
          variant="flat"
          selectedKeys={selectedKey}
          selectionMode="single"
          onSelectionChange={handleSelectChange}
        >
          <DropdownItem key="newest">Newest</DropdownItem>
          {selected === "active_polls" && (
            <DropdownItem key="endingsoon">Ending Soon</DropdownItem>
          )}
          <DropdownItem key="mostvoted">Most Voted</DropdownItem>
        </DropdownMenu>
      </Dropdown>
      <Tabs
        aria-label="Options"
        selectedKey={selected}
        onSelectionChange={handleTabChange}
        classNames={{
          tabList: "md:mx-auto mb-8"
        }}
      >
        <Tab key="active_polls" title="Active Polls">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 border border-[#C2C2C2] px-4 md:px-8 py-6 rounded-xl">
            {renderContent(
              isLoadingActive,
              activePolls,
              VoteCard,
              "DAO-No Active Proposals Right Now!",
              handleVote
            )}
          </div>
        </Tab>
        <Tab key="voting_results" title="Voting Results">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 border border-[#C2C2C2] px-4 md:px-8 py-6 rounded-xl">
            {renderContent(
              isLoadingInactive,
              votingResults,
              ViewCard,
              "DAO-No Results Found!(For voting Results)"
            )}
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}
